/**
 * Mega Menu Button
 *
 * Header button to toggle the mega menu.
 * Shows badge with total pending count.
 */

import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import { useModel } from '@/context/AuthContext';
import { useMegaMenuSafe } from '@/context/MegaMenuContext';
import styles from './MegaMenu.module.less';

/**
 * Calculate total pending count from initialState
 */
const useTotalPendingCount = (): number => {
  const { initialState } = useModel('@@initialState');

  return useMemo(() => {
    if (!initialState) return 0;

    const counts = [
      initialState.payments,
      initialState.instructions,
      initialState.craFormInstructions,
      initialState.digitalDepositPending,
      initialState.digitalDepositRejected,
      initialState.digitalWithdrawPending,
      initialState.cardApplicationCount,
      initialState.cardPendingEmbossedCount,
      initialState.solutionApplications,
      // New pending counts
      initialState.cashInOutPending,
      initialState.earnSubscriptionsPending,
      initialState.securitiesTransfersPending,
      initialState.fundInstructionsPending,
      initialState.preciousMetalPending,
    ];

    return counts.reduce((sum, count) => sum + (count || 0), 0);
  }, [initialState]);
};

export const MegaMenuButton: React.FC = () => {
  const megaMenu = useMegaMenuSafe();
  const totalPending = useTotalPendingCount();

  // Detect OS for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  const handleClick = () => {
    megaMenu?.toggle();
  };

  if (!megaMenu) {
    return null;
  }

  return (
    <Tooltip title={`Menu (${shortcutKey}+M)`} placement="bottom">
      <button
        type="button"
        className={`${styles.megaMenuButton} ${megaMenu.isOpen ? styles.active : ''}`}
        onClick={handleClick}
        aria-label="Open menu"
        aria-expanded={megaMenu.isOpen}
      >
        <AppstoreOutlined className={styles.megaMenuButtonIcon} />
        {totalPending > 0 && <span className={styles.megaMenuButtonBadge}>{totalPending > 99 ? '99+' : totalPending}</span>}
      </button>
    </Tooltip>
  );
};

export default MegaMenuButton;
